/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Skeleton } from '@mui/material';
import type { Optional } from '@poweralpha/common';
import { lowerCase, round } from 'lodash';
import { useCallback } from 'react';
import { toast } from '../../paToast';
import { AddFileButton, type AddFileButtonProps } from '../buttons';
import { useFieldContext } from './formContext';

interface Props extends Optional<Omit<AddFileButtonProps, 'fileAdded' | 'handleChange'>, 'id'> {
  maxSize?: number;
  acceptedExtensions?: Array<string>;
  isLoading?: boolean;
}

function FormFileButton(props: Props) {
  const { maxSize = Number.POSITIVE_INFINITY, acceptedExtensions, ...rest } = props;
  const field = useFieldContext<File | Array<File> | null>();
  const id = props.id ?? field.name.split('.').at(-1) ?? '';

  const handleAddFile = useCallback(
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: not that bad
    (fileList: FileList) => {
      if (fileList.length === 0) return;
      const files = props.multiple ? fileList : [fileList[0]];
      for (const file of files) {
        if (file.size > maxSize) {
          toast.error(`File size should not exceed 50mb (received: ${round(file.size / 1e6)}mb)`);
          return;
        }
        const ext = lowerCase(file.name.split('.').at(-1));
        if (acceptedExtensions ? !acceptedExtensions.includes(ext) : false) {
          toast.error(`File extension not recognized (${ext})`);
          return;
        }
      }
      field.handleChange(props.multiple ? Array.from(fileList) : fileList[0]);
    },
    [props.multiple, acceptedExtensions, maxSize, field],
  );

  if (props.isLoading) {
    return <Skeleton variant="rounded" height={40} width={117} />;
  }

  return (
    <AddFileButton
      {...rest}
      id={id}
      handleChange={handleAddFile}
      fileAdded={
        Array.isArray(field.state.value) ? field.state.value.length > 0 : Boolean(field.state.value)
      }
    />
  );
}

export default FormFileButton;
